import { useState, useEffect, useMemo } from "react";
import Form from "../../../Components/Form";
import TabBox from "../../../Components/Tabbox/TabBox";
import PortfoGroupUserMultiSelectDropdown from "../../../Components/Dropdown/Users/PortfoGroupUserMultiSelectDropdown";
import CustomDynamicDropdown from "../../../Components/Dropdown/CustomDropdown/CustomDynamicDropdown";
import StaticDropdown from "../../../Components/Dropdown/StaticDropdown";
import TextInput from "../../../Components/TextInput";
import Button from "../../../Components/Button";
import Modal from "../../../Components/Modal/Modal";
import {
  IStepOptionDropdownItem,
  StepInstrumentType,
  StepOptionOrderType,
  StepOrderSide,
  VolumeCalculationType,
} from "../../../Interfaces/stepInterfaces";
import {
  IManualAddOrderFormValues,
  IManualOrderPreview,
} from "../../../Interfaces/manualInterfaces";
import SahraConnections from "../../Sahra/SahraConnections";
import { toast } from "react-toastify";
import {
  addManualShekarOrder,
  getPreview,
} from "../../../Requests/manualRequests";
import { useAppDispatch } from "../../../App/hooks";
import {
  onManualOrderFormValuesChange,
  setManualOrderFormValues,
  setManualOrderTableFetchDataFlag,
} from "../../../Features/Manual/manualSlice";
import { getStepInstruments } from "../../../Requests/stepRequests";
import PreviewManualOrderTable from "../../../Tables/Manual/PreviewManualOrderTable";
import PreviewVolumeSummaryTable from "../../../Tables/Order/PreviewVolumeSummaryTable";
import PreviewWarningsTable from "../../../Tables/Order/PreviewWarningsTable";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { CheckBox, TimePicker } from "src/Components";
import {
  getManualScheduledExecutionTime,
  getManualTimePickerValue,
} from "./manualScheduleUtils";

interface IProps {
  formValues: IManualAddOrderFormValues | null;
}

enum ModalMode {
  SahraOrderTable,
  OrderPreview,
}

const SingleOrder = ({ formValues }: IProps) => {
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<IManualOrderPreview | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>(
    ModalMode.SahraOrderTable,
  );

  const [instrument, setInstrument] = useState<IStepOptionDropdownItem | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    setInstrument({
      id: formValues?.instrumentId || null,
      type: formValues?.instrumentType || null,
    } as IStepOptionDropdownItem);
  }, [formValues?.instrumentId, formValues?.instrumentType]);

  const modalCloseHandler = () => {
    setIsModalOpen(false);
  };

  const changeHandler = (
    name: string,
    value: any,
    name2?: string,
    value2?: any,
  ) => {
    dispatch(onManualOrderFormValuesChange({ name, value, name2, value2 }));
  };

  const checkboxChangeHandler = (name: string, checked: boolean) => {
    changeHandler(name, checked);
  };

  const timeChangeHandler = (
    name: string,
    value: { timeString: string; ISOString: string | null },
  ) => {
    changeHandler(name, getManualScheduledExecutionTime(value));
  };

  const getOrderSideChildren = () => [
    {
      index: 1,
      title: "خرید",
      value: StepOrderSide.Buy,
      className:
        formValues?.orderSide === StepOrderSide.Buy
          ? "!border-2 !border-green-600 !text-white hover:!border-green-700 !bg-green-600 hover:!bg-green-700 "
          : "!border-2 !border-gray-300 !text-gray-300 hover:!border-gray-400 hover:!text-gray-400",
    },
    {
      index: 2,
      title: "فروش",
      value: StepOrderSide.Sell,
      className:
        formValues?.orderSide === StepOrderSide.Sell
          ? "!border-2 !border-red-700 !text-white hover:!border-red-800 !bg-red-700 hover:!bg-red-800"
          : "!border-2 !border-gray-300 !text-gray-300 hover:!border-gray-400 hover:!text-gray-400",
    },
  ];

  const getOrderSideActiveTab = () => {
    if (formValues?.orderSide === StepOrderSide.Buy) return 1;
    return 2;
  };

  const checkSingleOrderBtnDisabled = () => {
    if (!formValues) return true;
    if (
      !formValues.instrumentId ||
      !formValues.orderSide ||
      !formValues.stepCount ||
      !formValues.fromPrice ||
      !formValues.toPrice ||
      !formValues.userIds ||
      !formValues.userIds.length ||
      !formValues.volume
    )
      return true;

    if (formValues.instrumentType === StepInstrumentType.Option ||
        formValues.instrumentType === StepInstrumentType.Future)
      if (!formValues.optionOrderType) return true;
    return false;
  };

  const getBtn = (onClick: () => void) => (
    <Button
      text={`${formValues?.orderSide === StepOrderSide.Buy ? "خرید" : "فروش"}`}
      onClick={onClick}
      variant="contained"
      className={`!mb-2 ${
        !checkSingleOrderBtnDisabled()
          ? formValues?.orderSide === StepOrderSide.Buy
            ? "green-btn"
            : "red-btn"
          : ""
      }`}
      disabled={checkSingleOrderBtnDisabled()}
    />
  );

  const addStepShekarOrderClickHandler = () => {
    if (!preview) return;
    const orderDto = getExecutablePreviewOrderDto();
    if (!orderDto) {
      toast.error("هیچ سفارشی با حجم معتبر وجود ندارد.");
      return;
    }

    setLoading(true);
    addManualShekarOrder(orderDto as unknown as IManualAddOrderFormValues)
      .then((resp) => {
        if (!resp.data) toast.error(resp.msg);
        else {
          toast.success(resp.msg);
          setIsModalOpen(false);
          dispatch(setManualOrderTableFetchDataFlag(true));
          dispatch(
            setManualOrderFormValues({
              instrumentId: preview.orderDto.instrumentId,
              optionOrderType: preview.orderDto.optionOrderType,
              orderSide: preview.orderDto.orderSide,
              instrumentType: preview.orderDto.instrumentType,
            } as IManualAddOrderFormValues),
          );
        }
      })
      .catch((e) => toast.error(e.response.data.value))
      .finally(() => {
        setLoading(false);
      });
  };

  const getExecutablePreviewRows = () =>
    (preview?.rows || []).filter((row) => row.totalVolume > 0);

  // Rows with no previewStatus were successfully read (even if the calculated volume is
  // legitimately zero) and belong in the main preview grid. Rows with a previewStatus
  // (Timeout/BrokenOrder/NoConnectedUser/Offline/Deduplicated) could not produce a real
  // order and are surfaced only in the warning table below.
  const getDisplayPreviewRows = () =>
    (preview?.rows || []).filter((row) => !row.previewStatus);

  const getExecutablePreviewOrderDto = () => {
    if (!preview) return null;

    const executableRows = getExecutablePreviewRows();
    const failedRows = (preview.rows || []).filter((row) => row.previewStatus);
    // Nothing executable AND nothing to log as failed either - genuinely nothing to send.
    if (!executableRows.length && !failedRows.length) return null;

    const executableUserIds = executableRows.map((row) => row.userId);
    const executableUserIdSet = new Set(executableUserIds);
    const executableVolumePerUser = Object.fromEntries(
      Object.entries(preview.orderDto.volumePerUser || {}).filter(([key]) =>
        executableUserIdSet.has(Number(key)),
      ),
    ) as Record<string, number>;

    return {
      ...preview.orderDto,
      userIds: executableUserIds,
      volumePerUser: executableVolumePerUser,
      failedRows,
    };
  };

  const removePreviewRowHandler = (userId: number) => {
    setPreview((current) => {
      if (!current) return current;

      const removedRow = current.rows.find((row) => row.userId === userId);
      const nextRows = current.rows.filter((row) => row.userId !== userId);
      const nextUserIds = current.orderDto.userIds.filter(
        (id) => id !== userId,
      );
      const nextTimedOutUserIds = (current.timedOutUserIds || []).filter(
        (id) => id !== userId,
      );
      const nextBrokenOrderUserIds = (current.brokenOrderUserIds || []).filter(
        (id) => id !== userId,
      );
      const nextVolumePerUser = Object.fromEntries(
        Object.entries(current.orderDto.volumePerUser || {}).filter(
          ([key]) => Number(key) !== Number(userId),
        ),
      ) as Record<string, number>;

      // The removed row only ever contributed to volumeSummary if it was executable
      // (no previewStatus and totalVolume > 0) - subtract its volume and engaged capital from that group.
      const nextVolumeSummary =
        removedRow && !removedRow.previewStatus && removedRow.totalVolume > 0
          ? (current.volumeSummary || [])
              .map((item) =>
                item.orderSide === removedRow.orderSide &&
                item.instrumentId === removedRow.instrumentId
                  ? {
                      ...item,
                      totalVolume: item.totalVolume - removedRow.totalVolume,
                      engagedCapitalAmount:
                        item.engagedCapitalAmount - removedRow.engagedCapitalAmount,
                    }
                  : item,
              )
              .filter((item) => item.totalVolume > 0)
          : current.volumeSummary;

      return {
        ...current,
        rows: nextRows,
        timedOutUserIds: nextTimedOutUserIds,
        brokenOrderUserIds: nextBrokenOrderUserIds,
        volumeSummary: nextVolumeSummary,
        orderDto: {
          ...current.orderDto,
          userIds: nextUserIds,
          volumePerUser: nextVolumePerUser,
        },
      };
    });
  };

  const getPreviewWarningRowsForTable = () =>
    (preview?.rows || []).flatMap((row) => {
      const userName = row.userName || row.userId.toString();

      if (!row.previewStatus) return [];

      const message =
        row.previewStatus === "Offline"
          ? "کاربر آنلاین نیست."
          : row.previewStatus === "Timeout"
            ? "دریافت پورتفو بعد از ۵ ثانیه Timeout شد."
            : row.previewStatus === "BrokenOrder"
              ? "اطلاعات پورتفو در دسترس نیست."
              : row.previewStatus === "NoConnectedUser"
                ? "حساب متصل (بورس کالا/بورس اوراق) برای این کاربر یافت نشد."
                : row.previewStatus === "Deduplicated"
                  ? "این کاربر با کاربر دیگری در لیست به یک حساب متصل است؛ سفارش فقط یک بار ثبت خواهد شد."
                  : row.previewMessage || "هشدار پیش‌نمایش سفارش";

      return [{ userName, message }];
    });
  const getSahraUserTable = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-3">
        <SahraConnections
          initialSearchParam={{
            userIds:
              getExecutablePreviewOrderDto()?.userIds ||
              formValues?.userIds ||
              [],
          }}
        />

        {getBtn(addStepShekarOrderClickHandler)}
      </div>
    );
  };

  const getOrderPreviewTable = () => {
    return (
      <div className="flex flex-col justify-start items-center gap-10">
        <PreviewVolumeSummaryTable values={preview?.volumeSummary} />
        <PreviewManualOrderTable
          values={getDisplayPreviewRows()}
          loading={previewLoading}
          onRemoveRow={removePreviewRowHandler}
        />
        {getBtn(() => {
          setModalMode(ModalMode.SahraOrderTable);
        })}
        <PreviewWarningsTable rows={getPreviewWarningRowsForTable()} />
      </div>
    );
  };

  const getModalUI = () => {
    switch (modalMode) {
      case ModalMode.SahraOrderTable:
        return getSahraUserTable();

      case ModalMode.OrderPreview:
        return getOrderPreviewTable();
    }
  };

  const instrumentChangeHandler = (
    _: string,
    value: IStepOptionDropdownItem | null,
  ) => {
    dispatch(
      setManualOrderFormValues({
        ...formValues,
        instrumentId: value?.id || null,
        instrumentType: value?.type || null,
        optionOrderType: null,
        volumeCalculationType: value?.type === StepInstrumentType.Future
          ? VolumeCalculationType.FixedAmount : formValues?.volumeCalculationType,
      } as IManualAddOrderFormValues),
    );
  };
  const volumeCalculationTypeOptions = [
    { id: VolumeCalculationType.FixedAmount, name: "مقدار ثابت" },
    {
      id: VolumeCalculationType.PortfolioPercent,
      name: "% از پورتفو",
    },
    {
      id: VolumeCalculationType.AssetPercent,
      name: "% از دارایی",
    },
    {
      id: VolumeCalculationType.TargetPortfolioPercent,
      name: "% هدف از پورتفو",
    },
    {
      id: VolumeCalculationType.BuyingPowerPercent,
      name: "% از قدرت خرید",
    },
    {
      id: VolumeCalculationType.FixedCapitalAmount,
      name: "سرمایه درگیر (مبلغ) ثابت",
    },
  ];

  const volume = useMemo(
    () =>
      volumeCalculationTypeOptions.find(
        (item) => item.id === formValues?.volumeCalculationType,
      )?.name ?? "حجم",
    [formValues?.volumeCalculationType],
  );

  return (
    <Form>
      <div className="w-80">
        {isModalOpen ? (
          <Modal title="ثبت سفارش" onClose={modalCloseHandler} UI={getModalUI()} />
        ) : null}
        <div className="flex flex-col justify-start items-center w-full px-2 gap-3 mt-3">
          <PortfoGroupUserMultiSelectDropdown
            name="userIds"
            label="نام اکانت ها"
            // className: "mt-3",
            onChange={changeHandler}
            value={formValues?.userIds}
            withTagBox
            extraLabel
          />
          <div className="flex flex-row justify-start items-center gap-5 w-full">
            <TabBox
              children={getOrderSideChildren()}
              activeTab={getOrderSideActiveTab()}
              onChange={(name, _, value) => changeHandler(name, value)}
              name="orderSide"
            />
          </div>
          <CustomDynamicDropdown
            dropdown={{
              name: "instrumentId",
              placeholder: "نماد",
              extraLabel: true,
            }}
            onChange={instrumentChangeHandler}
            getData={getStepInstruments}
            value={instrument}
          />
          {(formValues?.instrumentType === StepInstrumentType.Option ||
            formValues?.instrumentType === StepInstrumentType.Future) ? (
            <StaticDropdown
              dropdown={{
                name: "optionOrderType",
                placeholder: formValues?.instrumentType === StepInstrumentType.Future
                  ? "نوع معامله آتی" : "نوع معامله اختیار",
                extraLabel: true,
              }}
              onChange={changeHandler}
              options={[
                {
                  id: StepOptionOrderType.Offset,
                  name: "Offset",
                },
                {
                  id: StepOptionOrderType.Open,
                  name: "Open",
                },
              ]}
              value={formValues?.optionOrderType}
            />
          ) : null}

          <div className="flex flex-row justify-start items-center gap-3">
            <TextInput
              label="ابتدای بازه"
              name="fromPrice"
              onChange={changeHandler}
              value={formValues?.fromPrice?.toString()}
              kind="price"
              extraLabel
            />
            <TextInput
              label="انتهای بازه"
              name="toPrice"
              onChange={changeHandler}
              value={formValues?.toPrice?.toString()}
              kind="price"
              extraLabel
            />
          </div>
          <TextInput
            label="تعداد پلکان"
            name="stepCount"
            onChange={changeHandler}
            value={formValues?.stepCount?.toString()}
            kind="price"
            extraLabel
          />

          <StaticDropdown
            dropdown={{
              name: "volumeCalculationType",
              placeholder: "روش تعیین حجم",
              extraLabel: true,
            }}
            onChange={changeHandler}
            value={formValues?.volumeCalculationType}
            options={formValues?.instrumentType === StepInstrumentType.Future
              ? volumeCalculationTypeOptions.filter(item => item.id === VolumeCalculationType.FixedAmount)
              : volumeCalculationTypeOptions}
          />

          <TextInput
            label={volume}
            name="volume"
            onChange={changeHandler}
            value={formValues?.volume?.toString()}
            kind="priceWithDecimal"
            extraLabel
          />
          <TimePicker
            name="scheduledExecutionTime"
            placeholder="ساعت اجرا (اختیاری)"
            value={getManualTimePickerValue(formValues?.scheduledExecutionTime)}
            onChange={timeChangeHandler}
            extraLabel
          />
          <div className="flex flex-row justify-between items-center w-full">
            <CheckBox
              checked={!!formValues?.autoSellFund}
              label="فروش خودکار صندوق درآمد ثابت"
              name="autoSellFund"
              onChange={checkboxChangeHandler}
            />
          </div>
          {getBtn(() => {
            setIsModalOpen(true);
            setModalMode(ModalMode.OrderPreview);
            setPreview(null);
            setPreviewLoading(true);
            getPreview(formValues as IManualAddOrderFormValues)
              .then((pre) => setPreview(pre))
              .catch(packetErrorHandler)
              .finally(() => setPreviewLoading(false));
          })}
        </div>
      </div>
    </Form>
  );
};

export default SingleOrder;
